/**
 * components/statusChip.tsx
 * Generic tone-driven status chip (dot + label on a soft tint with hairline
 * border) — the single primitive behind every status pill in the app
 * (req status, candidate status, rerank status, portal/service status, etc.).
 * Feature-specific view-models map their enum → { label, tone } and render this.
 */
import React from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../context/themeContext';
import { toneColors, type Tone } from '../theme/tone';

export interface StatusChipProps {
  label: string;
  tone: Tone;
  /** Show the leading status dot (default true). */
  dot?: boolean;
}

export default function StatusChip({ label, tone, dot = true }: StatusChipProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const c = toneColors(colors, tone);
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 9,
        paddingVertical: 3,
        borderRadius: radii.pill,
        backgroundColor: c.bg,
        borderWidth: 1,
        borderColor: c.border,
        alignSelf: 'flex-start',
      }}
    >
      {dot && <View style={{ width: 6, height: 6, borderRadius: radii.pill, backgroundColor: c.fg }} />}
      <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.fg }}>{label}</Text>
    </View>
  );
}
